import { create } from 'zustand';

export interface AuthUserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
}

interface AuthState {
  user: AuthUserProfile | null;
  isAuthenticated: boolean;
  setAuth: (user: AuthUserProfile) => void;
  clearAuth: () => void;
  // Legacy compat aliases
  setUser: (user: AuthUserProfile | null) => void;
  login: (user: AuthUserProfile) => void;
  logout: () => void;
}

/**
 * The auth guard re-verifies the canonical identity every five minutes and on
 * each return to the tab, and each verification builds a fresh profile object.
 * Roughly a hundred components key effects on `user`, so a new reference for an
 * unchanged identity re-ran their reads (the worker roster alone: ~8k a day).
 * Keep the existing reference when every field is identical; any real change
 * (role, tenant, name, email) still publishes a new object.
 */
function sameProfile(previous: AuthUserProfile | null, next: AuthUserProfile | null): boolean {
  if (previous === next) return true;
  if (!previous || !next) return false;
  const keys = Object.keys(next) as (keyof AuthUserProfile)[];
  return (
    keys.length === Object.keys(previous).length &&
    keys.every((key) => Object.hasOwn(previous, key) && Object.is(previous[key], next[key]))
  );
}

// NOTE: JWT tokens must NOT be stored in client-side state.
// Supabase manages auth tokens via httpOnly cookies automatically.
export const useAuthStore = create<AuthState>((set) => {
  const publish = (user: AuthUserProfile | null) =>
    set((state) => ({
      user: sameProfile(state.user, user) ? state.user : user,
      isAuthenticated: !!user,
    }));
  return {
    user: null,
    isAuthenticated: false,
    setAuth: publish,
    clearAuth: () => set({ user: null, isAuthenticated: false }),
    // Legacy compat
    setUser: publish,
    login: publish,
    logout: () => set({ user: null, isAuthenticated: false }),
  };
});
